/**
 * Server-side counterpart to the (client-side) `DocumentBrowser`, wiring the
 * Documents tab to `vaultDocumentsSearch` (DV-330 / DV-248). It mirrors
 * fund-admin-frontend's `TicketsTable`: an AG Grid **server-side row model**
 * whose datasource translates the grid's filter/sort/scroll into a
 * `VaultDocumentsInput` and pages forward by cursor.
 *
 * This is a distinct component (not a wrapper of `DocumentBrowser`) because the
 * two use incompatible AG Grid row models — `DocumentBrowser` renders a
 * client-side `rowData` array, whereas the Documents-tab data path must fetch
 * per block from the server. The presentational vocabulary (columns per scope,
 * dash-formatted cells, domain/space naming) is kept aligned with
 * `DocumentBrowser`.
 *
 * Plug it into the existing integration hooks:
 *   const { globalLib, globalIdByKey } = useGlobalAttributes();
 *   const { spaces } = useVaultSpaces({ globalLib });
 *   <DocumentBrowserContainer
 *     spaces={spaces} domains={DOMAINS} globalLib={globalLib}
 *     globalIdByKey={globalIdByKey} space={activeSpace} domainFilter={activeDomains} />
 */
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { Grid, AgGrid, FileIcon } from "@jsq/ds/core";
import { semanticOf } from "#common/attributeKeys";
import { optionValueByIdFromCatalog } from "#common/graphql/Attribute/mappers";
import {
  type AttributeColumnMeta,
  apiVaultDocumentToVaultDocument,
  VaultDocumentsGraphqlDataSource,
} from "#common/graphql/Document";
import { dataTypeToValueType, domainIdToSlug } from "#common/graphql/Space";
import {
  type AttrStates,
  attrCellFormatter,
  attrCellTooltip,
  dashFormatter,
  rowAttrStates,
} from "./attributeCell";
import { attributeFilterDef, NO_FILTER, normalizeDateFilterModel } from "./attributeFilters";
import { allScopeGlobalColumns, spaceScopeGlobalColumns, withAttributeMeta } from "./constants";
import {
  clearDependentReferenceFilters,
  VaultClientFilter,
  VaultEntityFilter,
  VaultPositionFilter,
} from "./referenceFilters";
import { NEEDS_REVIEW, REVIEW_ROW_STYLE, StatusCell } from "./statusCell";
import * as styles from "./styles_vanilla.css";
import type { GlobalAttribute, Space, VaultDomain } from "#common/types/vault";
import type { VaultDocumentValidationStatus } from "#graphql/supergraphTypes";
import type { AttributeColumnSpec } from "./constants";

/** Rows sent to the grid: fixed presentation fields + dynamic attribute columns. */
type Row = {
  _id: string;
  _file: string;
  /** Quarantined — the row reads "Needs review" and is tinted. */
  _review: boolean;
  /** Tri-state per attribute COLUMN key (DV-504) — see `attributeCell.ts`. */
  _attrStates?: AttrStates;
  name: string;
  space: string | null;
  domain: string | null;
  /**
   * The single Status column: "Needs review" for a quarantined document,
   * otherwise the sharing status (Internal / External).
   */
  status: string;
  modified: string;
  [key: string]: unknown;
};

/** A grid column backed by an attribute, plus the metadata that picks its filter. */
type ColumnSpec = AttributeColumnSpec;

/** Page size — kept in sync with the grid's `cacheBlockSize` and `first`. */
const PAGE_SIZE = 20;

/**
 * The datasource, with `agDateColumnFilter`'s `"YYYY-MM-DD HH:mm:ss"` trimmed to
 * a calendar day on the way to the filter's `date`. See `normalizeDateFilterModel`.
 */
class VaultDocumentsDataSource<TRow> extends VaultDocumentsGraphqlDataSource<TRow> {
  override getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const filterModel = normalizeDateFilterModel(params.request.filterModel);
    if (filterModel === params.request.filterModel) return super.getRows(params);
    super.getRows({
      ...params,
      request: { ...params.request, filterModel: filterModel ?? null },
    });
  }
}

export type DocumentBrowserContainerProps = {
  /** Spaces (presentational) — used for space names and the space id -> domain map. */
  spaces: Space[];
  /** Domains (presentational) — used for domain display names. */
  domains: VaultDomain[];
  /** Curated global-attribute catalog (drives per-space column labels/order). */
  globalLib: GlobalAttribute[];
  /**
   * Maps a global attribute `key` -> backend `attributeId` (from
   * `useGlobalAttributes`). Required to translate attribute-column filters into
   * `attributeFilters`; columns whose key doesn't resolve are shown but not
   * server-filterable.
   */
  globalIdByKey?: Record<string, string>;
  /** Active space id, or null/undefined for "All spaces". */
  space?: string | null;
  /** Active domain filter as vault domain ids (mapped to slugs for the query). */
  domainFilter?: string[] | null;
  /**
   * Health-tab filter, sent as the query's `validationStatus`:
   * `["validated"]` for Filed documents, `["quarantined"]` for Documents
   * awaiting filing, and null/omitted for All documents (which must NOT
   * enumerate both — the enum has no `pending`, so listing them would drop
   * `pending` documents from the All tab). Changing it re-keys the grid, so the
   * row model re-requests block 0 with fresh cursors.
   */
  validationStatus?: VaultDocumentValidationStatus[] | null;
  /** Open a filed document (row click). */
  onOpenDoc?: (id: string) => void;
  /** Notified whenever the server reports a new total (for a count display). */
  onTotalCountChange?: (totalCount: number) => void;
  /**
   * Controlled 1-based page (e.g. mirrored to a `?page=` URL param). Defaults to
   * 1. Changing it navigates the grid; deep links / reloads to page N > 1 are
   * served by the datasource walking the forward cursor.
   */
  page?: number;
  /** Notified (1-based) when the user pages via the grid's pagination controls. */
  onPageChange?: (page: number) => void;
  /**
   * Bump to force a full server-side refresh (purge the grid's block cache and
   * reset the datasource cursors, then refetch from block 0). Needed after an
   * out-of-band mutation such as an upload completing: the datasource fetches
   * `no-cache`, so an Apollo cache update won't reach the grid, and a `page`
   * change alone won't refetch when the grid is already on that page.
   */
  refreshToken?: number;
};

/**
 * The reference semantics this grid can render and filter, backed by the shared
 * search components (`ClientSearch` / `EntitySearch`) and the in-repo
 * `PositionSearch`.
 */
const CLIENT_SEMANTIC = "client";
const ENTITY_SEMANTIC = "entity";
const INVESTOR_SEMANTIC = "investor";

function NameCell({ data, value }: { data?: Row; value?: unknown }) {
  if (!data) return null;
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is an opaque grid cell value, defensively stringified for display
  const label = value == null || value === "" ? "—" : String(value);
  return (
    <span
      className={data._review ? `${styles.nameCell} ${styles.nameReview}` : styles.nameCell}
    >
      <FileIcon extension={data._file} size="medium" />
      <span>{label}</span>
    </span>
  );
}

/** Resolve the per-scope column contract (mirrors `DocumentBrowser.resolveScope`). */
function resolveColumns(
  spaceObj: Space | null,
  globalLib: GlobalAttribute[],
): {
  showSpace: boolean;
  globalCols: ColumnSpec[];
  localCols: ColumnSpec[];
} {
  if (!spaceObj) {
    return {
      showSpace: true,
      // Carry each facet's catalog data type through so `attrCol` can pick a
      // filter control for it.
      globalCols: withAttributeMeta(allScopeGlobalColumns(globalLib), globalLib),
      localCols: [],
    };
  }
  const localCols = spaceObj.locals.map((a) => ({
    key: `loc:${a.name}`,
    label: a.name,
    localName: a.name,
    dataType: a.dataType,
    // Unlike globals, a space-local select attribute does carry its options.
    values: a.options ?? null,
  }));
  return {
    showSpace: false,
    globalCols: spaceScopeGlobalColumns(spaceObj, globalLib),
    localCols,
  };
}

const DEFAULT_COL_DEF: AgGrid.ColDef<Row> = {
  resizable: true,
  // Most columns aren't server-sortable/filterable; opt in per column below.
  sortable: false,
  filter: false,
  minWidth: 120,
  flex: 1,
};

export function DocumentBrowserContainer({
  spaces,
  domains,
  globalLib,
  globalIdByKey = {},
  space = null,
  domainFilter,
  validationStatus = null,
  onOpenDoc,
  onTotalCountChange,
  page = 1,
  onPageChange,
  refreshToken = 0,
}: DocumentBrowserContainerProps) {
  const client = useApolloClient();

  const spaceObj = useMemo(
    () => (space ? (spaces.find((s) => s.id === space) ?? null) : null),
    [space, spaces],
  );
  const cols = useMemo(() => resolveColumns(spaceObj, globalLib), [spaceObj, globalLib]);

  // Lookups for row mapping.
  const domainBySpaceId = useMemo(
    () => Object.fromEntries(spaces.map((s) => [s.id, s.domain])),
    [spaces],
  );
  const globalKeyByAttributeId = useMemo(
    () => Object.fromEntries(Object.entries(globalIdByKey).map(([key, id]) => [id, key])),
    [globalIdByKey],
  );
  const localNameByAttributeId = useMemo(() => {
    const map: Record<string, string> = {};
    (spaceObj?.locals ?? []).forEach((l) => {
      map[l.id] = l.name;
    });
    return map;
  }, [spaceObj]);
  const optionValueById = useMemo(
    () =>
      optionValueByIdFromCatalog(
        globalLib,
        spaces.flatMap((s) => s.locals),
      ),
    [globalLib, spaces],
  );

  const spaceNameById = useMemo(
    () => new Map(spaces.map((s) => [s.id, s.name] as const)),
    [spaces],
  );
  const domainNameById = useMemo(
    () => new Map(domains.map((d) => [d.id, d.name] as const)),
    [domains],
  );

  // AG Grid attribute-column id -> backend attribute descriptor (server filters).
  const attributeColumns = useMemo(() => {
    const map: Record<string, AttributeColumnMeta> = {};
    const byKey = new Map(globalLib.map((a) => [a.key, a] as const));
    cols.globalCols.forEach((c) => {
      const attributeId = globalIdByKey[c.key];
      const dataType = byKey.get(c.key)?.dataType;
      if (attributeId && dataType) {
        map[c.key] = {
          attributeId,
          valueType: dataTypeToValueType(dataType),
        };
      }
    });
    cols.localCols.forEach((c) => {
      const local = spaceObj?.locals.find((l) => l.name === c.localName);
      if (local) {
        map[c.key] = {
          attributeId: local.id,
          valueType: dataTypeToValueType(local.dataType),
          // The translator drops these unless the query names exactly one space,
          // which is the same condition under which they get a column at all.
          spaceLocal: true,
        };
      }
    });
    return map;
  }, [cols, globalLib, globalIdByKey, spaceObj]);

  /**
   * The Client, Entity, and Investor columns in the current scope. All are needed
   * by id: the Entity filter is scoped by the Client filter's model; the Position
   * filter is scoped by both Client and Entity. An entity's name can only be
   * looked up alongside its owning arena.
   */
  const referenceCols = useMemo(() => {
    const bySemantic = (semantic: string) =>
      cols.globalCols.find((c) => semanticOf(c.key) === semantic)?.key;
    return {
      clientColId: bySemantic(CLIENT_SEMANTIC),
      entityColId: bySemantic(ENTITY_SEMANTIC),
      investorColId: bySemantic(INVESTOR_SEMANTIC),
    };
  }, [cols]);

  const mapNode = useCallback(
    (node: Parameters<typeof apiVaultDocumentToVaultDocument>[0]): Row => {
      const doc = apiVaultDocumentToVaultDocument(node, {
        domainBySpaceId,
        globalKeyByAttributeId,
        localNameByAttributeId,
        optionValueById,
      });
      const review = doc.lifecycle === "quarantined";
      const row: Row = {
        _id: doc.id,
        _file: doc.file,
        _review: review,
        name: doc.name,
        space: spaceNameById.get(doc.space) ?? null,
        domain: domainNameById.get(doc.domain) ?? (doc.domain || null),
        // A quarantined document reports what it needs, not how it's shared; every
        // other lifecycle state falls through to the sharing status.
        status: review ? NEEDS_REVIEW : doc.status,
        modified: doc.modified,
        // Tri-state (DV-504), re-keyed from attribute names to column keys so
        // the cell formatter can tell "—" (bound, empty) from "N/A" (unbound).
        _attrStates: rowAttrStates(doc.attrStates, {
          globalKeys: cols.globalCols.map((c) => c.key),
          locals: cols.localCols,
        }),
      };
      cols.globalCols.forEach((c) => {
        row[c.key] = doc.attrs[c.key] ?? null;
      });
      cols.localCols.forEach((c) => {
        row[c.key] = (c.localName && doc.local[c.localName]) || null;
      });
      return row;
    },
    [
      cols,
      domainBySpaceId,
      globalKeyByAttributeId,
      localNameByAttributeId,
      optionValueById,
      spaceNameById,
      domainNameById,
    ],
  );

  const domainSlugs = useMemo(
    () => (domainFilter?.length ? domainFilter.map((id) => domainIdToSlug(id)) : null),
    [domainFilter],
  );

  // Serialized so a new array literal with the same statuses doesn't rebuild the
  // datasource (and refetch) on every parent render.
  const statusKey = validationStatus?.length ? [...validationStatus].sort().join(",") : "";
  const statuses = useMemo(
    () => (statusKey ? (statusKey.split(",") as VaultDocumentValidationStatus[]) : null),
    [statusKey],
  );

  // Rebuild the datasource whenever the query context (scope/filters) changes;
  // the grid re-requests block 0 with a fresh cursor map.
  const datasource = useMemo(
    () =>
      new VaultDocumentsDataSource<Row>(client, {
        mapNode,
        pageSize: PAGE_SIZE,
        spaceIds: spaceObj ? [spaceObj.id] : null,
        domain: domainSlugs,
        validationStatus: statuses,
        attributeColumns,
        onTotalCountChange: (n) => {
          onTotalCountChange?.(n);
        },
      }),
    [client, mapNode, spaceObj, domainSlugs, statuses, attributeColumns, onTotalCountChange],
  );

  const columnDefs = useMemo<AgGrid.ColDef<Row>[]>(() => {
    const defs: AgGrid.ColDef<Row>[] = [
      {
        headerName: "Document name",
        field: "name",
        pinned: "left",
        flex: 2,
        minWidth: 320,
        // Filename is server-sortable; free-text filtering is not (no server field).
        sortable: true,
        filter: false,
        cellRenderer: NameCell,
      },
    ];

    if (cols.showSpace) {
      // Space & domain are driven by the topbar filters (spaceIds/domain args),
      // so filtering stays display-only here, but the backend's `orderBy` enum
      // does support sorting on both (space_name / domain_slug).
      defs.push({
        headerName: "Space",
        field: "space",
        minWidth: 180,
        sortable: true,
        valueFormatter: dashFormatter,
      });
      defs.push({
        headerName: "Domain",
        field: "domain",
        minWidth: 150,
        sortable: true,
        valueFormatter: dashFormatter,
      });
    }

    /**
     * Reference cells render directly from `row[key]`, which `attributeValueToString`
     * now populates with the name(s) from the federated `valueArenaList` /
     * `valueEntityList` / `valueLpUserList` (via nested `account.name`) fields —
     * no second query needed.
     */
    const referenceValueGetter =
      (key: string) =>
      (p: { data?: Row }): string | null => {
        const text = p.data?.[key];
        return typeof text === "string" && text ? text : null;
      };

    /**
     * The filter for an attribute column.
     *
     * Client and Entity get the shared typeahead filters — the same controls
     * `fund-admin-frontend`'s `data-vault/entities` grid uses, with Entity
     * declaring its dependency on Client so picking a client scopes the entity
     * search. Everything else follows its data type through `attributeFilterDef`,
     * which offers only what the backend's operator table accepts.
     */
    const filterFor = (c: ColumnSpec): AgGrid.ColDef<Row> => {
      // No resolvable `attributeId` means no server predicate at all.
      if (!attributeColumns[c.key]) return NO_FILTER;
      if (c.key === referenceCols.clientColId) {
        return { filter: VaultClientFilter };
      }
      if (c.key === referenceCols.entityColId) {
        // The entity search is only usable once a client scopes it — main rejects
        // an entity query that names no arena. With no Client column in this
        // scope there is nothing to satisfy that dependency with, and offering
        // the filter anyway leaves a control stuck on "Please select the client
        // first" with no client filter anywhere in the panel. No filter is the
        // honest answer; a permanently disabled one is not.
        return referenceCols.clientColId
          ? {
              filter: VaultEntityFilter,
              filterParams: { dependentFilter: referenceCols.clientColId },
            }
          : NO_FILTER;
      }
      if (c.key === referenceCols.investorColId) {
        // The position search requires both a client and an entity to scope it.
        // With either upstream absent the search would render permanently stuck
        // on its disabled state; no filter is the honest answer.
        const { clientColId, entityColId } = referenceCols;
        return clientColId && entityColId
          ? {
              filter: VaultPositionFilter,
              filterParams: {
                dependentArenaFilter: clientColId,
                dependentEntityFilter: entityColId,
              },
            }
          : NO_FILTER;
      }
      return attributeFilterDef(c.dataType, { mode: "server", values: c.values });
    };

    const attrCol = (c: ColumnSpec): AgGrid.ColDef<Row> => {
      const isReference = c.dataType === "Reference";
      return {
        headerName: c.label,
        field: c.key,
        minWidth: 150,
        ...(isReference ? { valueGetter: referenceValueGetter(c.key) } : {}),
        // Tri-state (DV-504): "—" for bound-but-empty, "N/A" for not bound to
        // the document's space, with a tooltip explaining either blank.
        valueFormatter: attrCellFormatter,
        tooltipValueGetter: attrCellTooltip,
        // Hidden by default in the All-spaces scope (see `allScopeGlobalColumns`);
        // still listed in the Columns / Filters tool panels.
        hide: c.hide ?? false,
        // Attribute columns aren't server-sortable — `orderBy` is a fixed enum.
        sortable: false,
        ...filterFor(c),
      };
    };

    cols.globalCols.forEach((c) => defs.push(attrCol(c)));
    cols.localCols.forEach((c) => defs.push(attrCol(c)));

    // One Status column for both readings of "status": a quarantined document
    // shows "Needs review", every other document shows how it's shared
    // (Internal / External). Always present — the awaiting-filing tab is defined
    // by this value, so it can't be conditional on the space's contract. Neither
    // reading is server-filterable (`VaultDocumentsInput` has no sharing field,
    // and the lifecycle is already pinned by the tab), so it's display-only.
    defs.push({
      headerName: "Status",
      field: "status",
      pinned: "right",
      minWidth: 150,
      filter: false,
      sortable: false,
      cellRenderer: StatusCell,
    });

    defs.push({
      headerName: "Last modified",
      field: "modified",
      pinned: "right",
      minWidth: 150,
      sortable: true,
      filter: false,
      valueFormatter: dashFormatter,
    });

    return defs;
  }, [cols, attributeColumns, referenceCols]);

  // Re-key the grid on scope change so it fully re-initializes the row model.
  // The health tab is part of the scope: switching tabs changes the row set, and
  // a re-key also drops the previous tab's pagination state.
  const scopeKey = `${spaceObj ? spaceObj.id : "all"}:${domainSlugs?.join(",") ?? ""}:${statusKey}`;

  // Columns + Filters tool panels, mirroring `DocumentBrowser`'s sidebar. (No
  // bulk-actions panel: SSRM selection only covers loaded pages, so cross-page
  // bulk actions would be misleading here.)
  const sideBar = useMemo<AgGrid.SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
      ],
    }),
    [],
  );

  // ---- Controlled pagination <-> AG Grid reconciliation ---------------------
  // The grid owns the page internally; we mirror it to/from the `page` prop.
  // `gridPageRef` tracks the page we last observed the grid on, so we can tell a
  // user-driven change (grid moved) from a prop-driven one (URL back/forward,
  // deep link) and avoid a feedback loop.
  const gridApiRef = useRef<AgGrid.GridApi<Row> | null>(null);
  const desiredPageRef = useRef(page);
  desiredPageRef.current = page;
  const gridPageRef = useRef(1);

  const reconcilePage = useCallback(() => {
    const api = gridApiRef.current;
    // Tab/space changes re-key the grid. AG Grid still fires paginationChanged
    // while tearing down; calling the dead API yields NaN and a console warning.
    if (!api || api.isDestroyed()) return;

    const zeroBased = api.paginationGetCurrentPage();
    const gridPage = zeroBased + 1; // 0-based → 1-based
    if (!Number.isInteger(gridPage) || gridPage < 1) return;

    const desired = Math.max(1, desiredPageRef.current);

    if (gridPage !== gridPageRef.current) {
      // The grid moved (user clicked a page control): propagate upward.
      gridPageRef.current = gridPage;
      if (gridPage !== desired) onPageChange?.(gridPage);
      return;
    }
    // The prop wants a different page: navigate there once it exists.
    const totalPages = api.paginationGetTotalPages();
    if (desired !== gridPage && totalPages > 0 && desired - 1 < totalPages) {
      gridPageRef.current = desired;
      api.paginationGoToPage(desired - 1);
    }
  }, [onPageChange]);

  // Apply a prop change (e.g. browser back/forward on `?page=`) to the grid.
  useEffect(() => {
    reconcilePage();
  }, [page, reconcilePage]);

  // A bumped `refreshToken` (e.g. an upload completing) purges the grid's block
  // cache and resets the datasource cursors so block 0 is refetched from the
  // server — the SSRM otherwise serves its cached blocks and never sees the new
  // rows. Skipped on mount (the grid already fetches then).
  const refreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (refreshTokenRef.current === refreshToken) return;
    refreshTokenRef.current = refreshToken;
    datasource.refresh();
    const api = gridApiRef.current;
    if (api && !api.isDestroyed()) api.refreshServerSide({ purge: true });
  }, [refreshToken, datasource]);

  return (
    <div className={styles.results}>
      <div className={styles.gridWrap}>
        <Grid<Row>
          key={scopeKey}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          getRowId={(p) => String(p.data._id)}
          rowHeight={48}
          headerHeight={42}
          getRowStyle={(p) => (p.data?._review ? REVIEW_ROW_STYLE : undefined)}
          rowModelType="serverSide"
          serverSideDatasource={datasource}
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={10}
          // Visible page controls. `paginationPageSize` is kept equal to
          // `cacheBlockSize` so one page == one server fetch. The datasource
          // walks the forward cursor, so jumping to (or deep-linking) a page
          // beyond what's been visited is served rather than failing.
          pagination
          paginationPageSize={PAGE_SIZE}
          sideBar={sideBar}
          // REQUIRED by the Client / Entity filters, not a style preference.
          // Without it AG Grid instantiates a custom React component through the
          // LEGACY path and hands it `IFilterParams` (`filterChangedCallback`,
          // `valueGetter`, …) — there is no `onModelChange` and no `model`, so the
          // filter throws "onModelChange is not a function" the moment a client is
          // picked. `reactiveCustomComponents` is what selects the declarative API
          // that `useGridFilter` and those props belong to; `data-vault/entities`
          // sets it on the grid its ClientFilter/EntityFilter run in for the same
          // reason. AG Grid also warns that omitting it is deprecated.
          reactiveCustomComponents
          suppressCellFocus
          onGridReady={(e) => {
            gridApiRef.current = e.api;
            reconcilePage();
          }}
          onGridPreDestroyed={(e) => {
            if (gridApiRef.current === e.api) gridApiRef.current = null;
          }}
          onPaginationChanged={reconcilePage}
          // An entity belongs to one client and a position to one entity, so
          // upstream changes strand any selections made under the previous values.
          onFilterChanged={(e) => clearDependentReferenceFilters(e, referenceCols)}
          onRowClicked={(e) => {
            if (e.data) onOpenDoc?.(e.data._id);
          }}
        />
      </div>
    </div>
  );
}
